import { FC, useEffect, useState, useMemo, useRef, useContext, JSX } from "react";
import { useHistory } from "react-router-dom"
import { Link } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";

import { AccordionContainer, Accordion, BreadcrumbTemplateEnum, Breadcrumbs, FilterCriteriaModel, Page, PageHeader, PageHeaderLeft, PageHeaderRight, PageInner, PageSection, breadcrumbTemplates } from "@app/shared";
import { ColumnConfig, SortDirections, Waffles, wafflesKeyColumnName, WafflesPaginationProps } from "@app/shared/waffles";
import { SortingMetadata } from "@app/shared/waffles/exports";
import { QESButton, QESCheckbox, QESTextInput } from "@app/shared/qes-standards";
import { CheckboxDropdown, DropdownItem } from "@app/shared/checkbox-dropdown";

import { RoleRestricted, Roles, UserContext } from "@app/user";

import { ResourceType } from "../resources";
import CreateResourceModel from "@app/resources/createResourceModel";
import CreateFilteredResourceModel from "@app/resources/createFilteredResourceModel";

import ProviderSetIconBox from "./ProviderSetIconBox";
import { ProviderSetSimpleModel, ProviderSetSimpleListModel } from "./providerSet";
import ProviderSetService from "./providerSetService";
import { ProviderSetsRequestType } from "./enumerations";
import CreatePanelContent from "./CreatePanelContent";
import { FlyoutLink } from "../shared/Flyouts";
import { QesIcons } from "@app/shared/icons/qesIcons";

interface ProviderSetTableData {
  [wafflesKeyColumnName]: string;
  name: JSX.Element;
  description: string;
  lastActivationDate: string;
  status: JSX.Element;
  displayType: string;
  isFiltered: boolean;
  rowIsDisabled: boolean;
  uniqueProviders?: number;
  uniqueLocations?: number;
  totalAccessPoints?: number;
  sourceProviderSetId?: number;
}

const providerSetColumnConfig: ColumnConfig = {
  description: {
    displayText: "Description",
    testId: "description",
  },
  displayType: {
    displayText: "Type",
    testId: "displayType",
  },
  lastActivationDate: {
    displayText: "Last Activated Date",
    testId: "lastActivatedDate",
  },
  name: {
    displayText: "Name",
    testId: "name",
  },
  rowIsDisabled: {
    displayText: "",
    hidden: true,
    testId: "",
  },
  sourceProviderSetId: {
    displayText: "",
    hidden: true,
    testId: "",
  },
  status: {
    displayText: "Status",
    testId: "status",
  },
  totalAccessPoints: {
    displayText: "Total Access Points",
    testId: "num-access-points",
    tooltip: "View network details to generate the summary counts.",
  },
  uniqueLocations: {
    displayText: "Unique Locations",
    testId: "num-unique-locations",
    tooltip: "View network details to generate the summary counts.",
  },
  uniqueProviders: {
    displayText: "Unique Providers",
    testId: "num-unique-providers",
    tooltip: "View network details to generate the summary counts.",
  },
};

interface PageSettings {
  skip: number,
  sortDetails: SortingMetadata<ProviderSetTableData>,
}

export const ProviderSets: FC<React.PropsWithChildren<unknown>> = () => {

  const providerSetService = new ProviderSetService();
  const urlParams = new URLSearchParams(window.location.search);
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [sidePanelLoading, setSidePanelLoading] = useState(false);
  const [activeSwitcherText, setActiveSwitcherText] = useState("Active Networks");
  const [activeSwitcherSubtext, setActiveSwitcherSubtext] = useState("(toggle for archived)");
  const [archivedProviderSets, setArchivedProviderSets] = useState<ProviderSetTableData[]>([]);
  const [myProviderSets, setMyProviderSets] = useState<ProviderSetTableData[]>([]);
  const [sharedProviderSets, setSharedProviderSets] = useState<ProviderSetTableData[]>([]);
  const [systemProviderSets, setSystemProviderSets] = useState<ProviderSetTableData[]>([]);
  const [take] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);
  const [numRecordsDisplayed, setNumRecordsDisplayed] = useState(0);
  const [activeFetchRequest, setActiveFetchRequest] = useState(() => (urlParams.get("req") as ProviderSetsRequestType) ?? ProviderSetsRequestType.NetworkProviders);
  const [showActiveProviderSets, setShowActiveProviderSets] = useState(() => (activeFetchRequest !== ProviderSetsRequestType.ArchivedNetworkProviders));
  const [nameFilter, setNameFilter] = useState(() => urlParams.get("name") ?? "");
  const [descriptionFilter, setDescriptionFilter] = useState(() => urlParams.get("desc") ?? "");
  const [typeFilter, setTypeFilter] = useState(() => urlParams.get("type") ?? "");
  const [statusFilter, setStatusFilter] = useState(() => urlParams.get("status") ?? "");
  const [networkTypeFilter, setNetworkTypeFilter] = useState<DropdownItem<ResourceType>[]>(() => getSelectedNetworkTypeFilterItems(urlParams.get("netType")));
  const [isFiltered, setIsFiltered] = useState(false);

  const [selectedActiveRecords, setSelectedActiveRecords] = useState<ProviderSetTableData[]>([]);
  const [selectedSharedRecords, setSelectedSharedRecords] = useState<ProviderSetTableData[]>([]);
  const user = useContext(UserContext);


  const [pageSettings, setPageSettings] = useState<PageSettings>(!!urlParams.get("pageSet") ? JSON.parse(urlParams.get("pageSet")) :
    {
      skip: 0,
      sortDetails: {
        sortDirection: SortDirections.None,
        sortedColumn: null,
      },
    });
  //Used to block certain useEffect hooks from firing on the first run before we can apply the filtering, paging, etc... from the page's query string
  const firstRun = useRef(true);

  const paginationProps: WafflesPaginationProps = {
    changePage: (pageNumber: number) => setPageSettings({ ...pageSettings, skip: (pageNumber - 1) * take }),
    recordsPerPage: take,
    skip: pageSettings.skip,
    totalRecords: totalRecords,
  }

  const breadcrumbs = breadcrumbTemplates[BreadcrumbTemplateEnum.ProviderSets];

  useEffect(() => {
    if (firstRun.current)
      return;

    if (showActiveProviderSets) {
      setActiveSwitcherSubtext("(toggle for archived)");
      setActiveSwitcherText("Active Networks");
      setActiveFetchRequest(ProviderSetsRequestType.NetworkProviders);
    } else {
      setActiveSwitcherSubtext("(toggle for active)");
      setActiveSwitcherText("Archived Networks");
      setActiveFetchRequest(ProviderSetsRequestType.ArchivedNetworkProviders);
    }
  }, [showActiveProviderSets]);

  useEffect(() => {
    if (firstRun.current)
      return;

    setShowActiveProviderSets(activeFetchRequest !== ProviderSetsRequestType.ArchivedNetworkProviders);
    clearFilter();
    setPageSettings({
      ...pageSettings,
      skip: 0,
      sortDetails: {
        sortDirection: SortDirections.None,
        sortedColumn: null,
      }
    });
  }, [activeFetchRequest]);

  useEffect(() => {
    if (firstRun.current)
      return;

    fetchProviderSets();
  }, [pageSettings]);

  useEffect(() => {
    firstRun.current = false;
    fetchProviderSets();
  }, [])

  const handleSort = (data: ProviderSetTableData[], newSortedColumn: keyof ProviderSetTableData, direction: SortDirections) => {
    setPageSettings({
      ...pageSettings,
      skip: 0,
      sortDetails: {
        sortDirection: direction,
        sortedColumn: newSortedColumn,
      }
    });
    return data;
  }

  const handleCheckBoxChange = (selectedKeys: string[], isBulk: boolean, providerSets: ProviderSetTableData[],
    setProviderSets: React.Dispatch<React.SetStateAction<ProviderSetTableData[]>>
    , setSelectedRecords: React.Dispatch<React.SetStateAction<ProviderSetTableData[]>>) => {

    let selectedRecords = providerSets.filter(p => selectedKeys.includes(p[wafflesKeyColumnName]));

    if (isBulk) {
      providerSets.forEach(p => p.rowIsDisabled = false);

      if (selectedKeys.length > 0 && selectedKeys.length < providerSets.length) {
        selectedRecords = providerSets;
      }
    }

    const myNewProviderSets = [...providerSets];

    if (!isBulk) {
      setTableButtonDisabledStatus(myNewProviderSets, selectedRecords);
    }
    else {
      const parentSetIds = selectedRecords.filter(p => p.sourceProviderSetId ?? 0 === 0).map(p => p[wafflesKeyColumnName]);
      myNewProviderSets.forEach(p => {
        if (p.sourceProviderSetId > 0 && parentSetIds.includes(p[wafflesKeyColumnName])) {
          const idx = selectedRecords.indexOf(p);
          selectedRecords.splice(idx, 1);
          p.rowIsDisabled = true;
        }
      });
    }

    setProviderSets(myNewProviderSets);
    setSelectedRecords([...selectedRecords]);
  }

  const handleActiveCheckboxChange = (selectedKeys: string[], isBulk: boolean) => {
    handleCheckBoxChange(selectedKeys, isBulk, myProviderSets, setMyProviderSets, setSelectedActiveRecords);

  }

  const handleSharedCheckboxChange = (selectedKeys: string[], isBulk: boolean) => {
    handleCheckBoxChange(selectedKeys, isBulk, sharedProviderSets, setSharedProviderSets, setSelectedSharedRecords);
  }

  const setTableButtonDisabledStatus = (providerSetList: ProviderSetTableData[], selectedList: ProviderSetTableData[]) => {
    providerSetList.forEach(r => r.rowIsDisabled = selectedList.some(s => r.key === s.sourceProviderSetId.toString() || r.sourceProviderSetId.toString() === s.key));
  }

  const showCheckbox = () => {
    return user.roles.includes(Roles.CCIIO_QHP_NETWORK_REPORTER);
  }

  const myProviderSetsBody = useMemo(() => {
    let body = null;
    if ((!!myProviderSets && myProviderSets.length > 0) || loading) {
      body = (
        <Waffles<ProviderSetTableData>
          columns={providerSetColumnConfig}
          customSort={handleSort}
          data={myProviderSets}
          enablePagination={true}
          fullWidth={true}
          loading={loading && activeFetchRequest === ProviderSetsRequestType.NetworkProviders}
          passThroughSorting={true}
          paginationProps={paginationProps}
          testId="my-provider-set-waffles-table"
          selectedKeys={selectedActiveRecords.map(m => m.key)}
          showCheckboxes={showCheckbox()}
          handleCheckboxChange={handleActiveCheckboxChange}
        />
      );
    } else {
      body = (
        <p className="lead">There are no provider networks.</p>
      );
    }

    return body;
  }, [myProviderSets, loading, selectedActiveRecords]);

  const archivedProviderSetsBody = useMemo(() => {
    let body = null;
    if ((!!archivedProviderSets && archivedProviderSets.length > 0) || loading) {
      body = (
        <Waffles<ProviderSetTableData>
          columns={providerSetColumnConfig}
          customSort={handleSort}
          data={archivedProviderSets}
          enablePagination={true}
          fullWidth={true}
          loading={loading && activeFetchRequest === ProviderSetsRequestType.ArchivedNetworkProviders}
          passThroughSorting={true}
          paginationProps={paginationProps}
          testId="archived-provider-set-waffles-table"
        />
      );
    } else {
      body = (
        <p className="lead">There are no provider networks.</p>
      );
    }

    return body;
  }, [archivedProviderSets, loading]);

  const sharedProviderSetsBody = useMemo(() => {
    let body = null;
    if ((sharedProviderSets && sharedProviderSets.length > 0) || loading) {
      body = (
        <Waffles<ProviderSetTableData>
          columns={providerSetColumnConfig}
          customSort={handleSort}
          data={sharedProviderSets}
          enablePagination={true}
          fullWidth={true}
          loading={loading && activeFetchRequest === ProviderSetsRequestType.SharedProviders}
          passThroughSorting={true}
          paginationProps={paginationProps}
          testId="shared-provider-set-waffles-table"
          selectedKeys={selectedSharedRecords.map(m => m.key)}
          showCheckboxes={showCheckbox()}
          handleCheckboxChange={handleSharedCheckboxChange}
        />);
    } else {
      body = (
        <p className="lead">There are no shared provider networks.</p>
      );
    }

    return body;
  }, [sharedProviderSets, loading, selectedSharedRecords]);

  const systemProviderSetsBody = useMemo(() => {
    let body = null;
    if ((systemProviderSets && systemProviderSets.length > 0) || loading) {
      body = (
        <Waffles<ProviderSetTableData>
          columns={providerSetColumnConfig}
          customSort={handleSort}
          data={systemProviderSets}
          enablePagination={true}
          fullWidth={true}
          loading={loading && activeFetchRequest === ProviderSetsRequestType.SystemProviders}
          passThroughSorting={true}
          paginationProps={paginationProps}
          testId="system-provider-set-waffles-table"
        />);
    } else {
      body = (
        <p className="lead">There are no system provider networks.</p>
      );
    }

    return body;
  }, [systemProviderSets, loading]);

  const fetchProviderSets = () => {
    setLoading(true);
    providerSetService.getProviderSetsByRequestType(activeFetchRequest, pageSettings.skip, take, pageSettings.sortDetails.sortDirection, pageSettings.sortDetails.sortedColumn, nameFilter, descriptionFilter, typeFilter, statusFilter, networkTypeFilter.map(x => x.key)).then((providerSets) => {
      fetchProviderSetsResponse(providerSets);
      setLoading(false);
    })
  }

  const fetchProviderSetsResponse = (response: ProviderSetSimpleListModel) => {

    switch (activeFetchRequest) {
      case ProviderSetsRequestType.NetworkProviders:
        const sets = createWafflesTable(response.providerSetSimpleList);
        setTableButtonDisabledStatus(sets, selectedActiveRecords);
        setMyProviderSets(sets);
        break;
      case ProviderSetsRequestType.ArchivedNetworkProviders:
        setArchivedProviderSets(createWafflesTable(response.providerSetSimpleList));
        break;
      case ProviderSetsRequestType.SharedProviders:
        const SharedSets = createWafflesTable(response.providerSetSimpleList);
        setTableButtonDisabledStatus(SharedSets, selectedSharedRecords);
        setSharedProviderSets(SharedSets);
        break;
      case ProviderSetsRequestType.SystemProviders:
        setSystemProviderSets(createWafflesTable(response.providerSetSimpleList));
        break;
    }

    let selectedNetworkItems = networkTypeFilter
    if (selectedNetworkItems.length === 0)
      selectedNetworkItems = getAllNetworkTypeDropdownItems();

    const isNetworkTypeFiltered = getAllNetworkTypeDropdownItems().length !== selectedNetworkItems.length;
    setIsFiltered(!!(nameFilter || descriptionFilter || typeFilter || statusFilter || isNetworkTypeFiltered));
    setNetworkTypeFilter(selectedNetworkItems);
    setTotalRecords(response.totalCount);
    setNumRecordsDisplayed(response.providerSetSimpleList.length);

    const searchParams = new URLSearchParams(window.location.search);

    if (activeFetchRequest !== ProviderSetsRequestType.NetworkProviders) {
      searchParams.set("req", activeFetchRequest);
    } else {
      searchParams.delete("req");
    }
    if (pageSettings.skip > 0 || pageSettings.sortDetails.sortDirection !== SortDirections.None) {
      searchParams.set("pageSet", JSON.stringify(pageSettings));
    } else {
      searchParams.delete("pageSet");
    }
    if (!!nameFilter) {
      searchParams.set("name", nameFilter);
    } else {
      searchParams.delete("name");
    }
    if (!!descriptionFilter) {
      searchParams.set("desc", descriptionFilter);
    } else {
      searchParams.delete("desc");
    }
    if (!!typeFilter) {
      searchParams.set("type", typeFilter);
    } else {
      searchParams.delete("type");
    }
    if (!!status) {
      searchParams.set("status", status);
    } else {
      searchParams.delete("status");
    }
    if (isNetworkTypeFiltered) {
      searchParams.set("netType", JSON.stringify(networkTypeFilter));
    } else {
      searchParams.delete("netType");
    }

    history.replace(history.location.pathname + "?" + searchParams.toString());
  }

  const handleProviderSetSelected = (providerSetId: number) => {
    history.push(`/provider-sets/${providerSetId}/detail`);
  }

  const createWafflesTable = (data: ProviderSetSimpleModel[]) => {
    return data.map((rec) => {
      const name = (
        <div key={rec.name} onClick={() => handleProviderSetSelected(rec.providerSetId)}>
          {ProviderSetIconBox(rec)}
        </div>
      );
      /* eslint-disable sort-keys */
      return ({
        key: rec.providerSetId.toString(),
        name,
        description: rec.description,
        isFiltered: rec.sourceProviderSetId ? true : false,
        displayType: rec.displayType,
        status: (<div key={rec.status} className={rec.status === "New version pending" || rec.status === "Publish pending" ? "text-warning" : ""}>{rec.status === "Unknown" ? "" : rec.status}</div>),
        lastActivationDate: (!!rec.lastActivationDate ? moment.utc(rec.lastActivationDate).local().format("MMM DD, YYYY h:mm:ss A") : "(none)"),
        uniqueProviders: rec.uniqueProviders,
        uniqueLocations: rec.uniqueLocations,
        totalAccessPoints: rec.totalAccessPoints,
        sourceProviderSetId: rec.sourceProviderSetId,
        rowIsDisabled: false
      });
      /* eslint-enable sort-keys */
    });
  }

  const handleSearchKeyUp = (e: React.KeyboardEvent) => {
    if (e.key.toLowerCase() === "enter") {
      handleSearch();
    }
  }

  const handleSearch = () => {
    setPageSettings({ ...pageSettings, skip: 0 });
  }

  const clearFilter = () => {
    setNameFilter("");
    setDescriptionFilter("");
    setTypeFilter("");
    setStatusFilter("");
    setIsFiltered(false);
    setNetworkTypeFilter(getAllNetworkTypeDropdownItems());

    setPageSettings({ ...pageSettings, skip: 0 });
  }

  const handleProviderSetDetailSave = async (name: string, description: string, isPrivate: boolean) => {
    const createModel: CreateResourceModel = {
      description: description,
      isPrivate: isPrivate,
      name: name,
    }

    setSidePanelLoading(true);
    providerSetService.addProviderSet(createModel)
      .then((response) => {
        setSidePanelLoading(false);
        history.push(`/provider-sets/${response.resource.resourceId}/detail`);
      }, () => {
        toast("An error occurred while creating this network.",
          {
            containerId: "z-index-9999",
            type: "error"
          }
        );
        setSidePanelLoading(false);
      }
      );
  }

  const handleFilteredProviderSetDetailSave = async (name: string, description: string, isPrivate: boolean, criteria: FilterCriteriaModel[], sourceProviderId: string, filterInvalidRecords: boolean) => {
    const createFilteredModel: CreateFilteredResourceModel = {
      criteria: criteria,
      description: description,
      filterInvalidRecords: filterInvalidRecords,
      isPrivate: isPrivate,
      name: name,
      sourceResourceId: sourceProviderId
    }

    setSidePanelLoading(true);
    providerSetService.addFilteredProviderSet(createFilteredModel)
      .then((response) => {
        setSidePanelLoading(false);
        history.push(`/provider-sets/${response.resource.resourceId}/detail`);
      }, () => {
        toast("An error occurred while creating this network.",
          {
            containerId: "z-index-9999",
            type: "error"
          }
        );
        setSidePanelLoading(false);
      }
      );
  }

  return (
    <>
      {(
        <Page documentTitle="Provider Networks">
          <Breadcrumbs items={breadcrumbs} />
          <PageInner>
            <PageHeader title="Provider Networks" iconName={QesIcons.FontAwesome.UserMd}>
              <PageHeaderLeft>
                <RoleRestricted allowedRoles={[Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN, Roles.RESOURCE_ADMIN]}>
                  <div className="ml-1">
                    <i className="fa fa-share text-muted mr-1" />
                    <Link
                      to={{
                        pathname: `/resources/share`,
                        state: {
                          breadcrumbItems: breadcrumbs,
                          isSingleResource: false,
                          resourceDescription: "Provider Network",
                          resourceTypes: [ResourceType.ProviderSet, ResourceType.FilteredProviderSet],
                        },
                      }}
                    >
                      Share
                    </Link>
                  </div>
                </RoleRestricted>
              </PageHeaderLeft>
              <PageHeaderRight>
                <RoleRestricted allowedRoles={[Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN, Roles.RESOURCE_ADMIN]}>
                  <div className="btn-group">
                    <FlyoutLink
                      classNames="qes-button-primary pr-2"
                      content={{
                        body: <CreatePanelContent
                          loading={sidePanelLoading}
                          handleSave={handleProviderSetDetailSave} />,
                        title: "Add a New Network"
                      }}
                      size="40"
                      testId="provider-sets__add-network-flyout--button"
                    >
                      <i className="fa fa-plus" /> Add Network
                    </FlyoutLink>
                    <div className="dropdown">
                      <QESButton qesTestId="provider-set_add-network" qesType="primary" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false"><i className="fa fa-chevron-down" /></QESButton>
                      <ul className="dropdown-menu dropdown-menu-right dropdown-animation">
                        <li className="p-0" key="1" >
                          <FlyoutLink
                            classNames="qes-button-secondary px-0 w-100"
                            content={{
                              body: <CreatePanelContent
                                createFiltered={true}
                                loading={sidePanelLoading}
                                handleSave={handleFilteredProviderSetDetailSave} />,
                              title: "Add a New Filtered Network",
                            }}
                            testId="provider-sets__add-filtered-network-flyout"
                          >
                            Add Filtered Network
                          </FlyoutLink>
                        </li>
                        <li className="p-0" key="2" >
                          <FlyoutLink
                            classNames="qes-button-secondary w-100"
                            content={{
                              body: <CreatePanelContent
                                loading={sidePanelLoading}
                                handleSave={handleProviderSetDetailSave} />,
                              title: "Add a New Network",
                            }}
                            size="40"
                            testId="provider-sets__add-network-flyout--dropdown"
                          >
                            Add Network
                          </FlyoutLink>
                        </li>
                      </ul>
                    </div>
                  </div>
                </RoleRestricted>
              </PageHeaderRight>
            </PageHeader>
            <PageSection>
              <div className="provider-sets-filters mb-3 mt-5">
                <QESTextInput containerClassName="provider-sets-filters__input" label="Name" testId="provider-sets-filter__name" value={nameFilter} onKeyUp={(e) => handleSearchKeyUp(e)} onChange={(e) => setNameFilter(e.target.value)} />
                <QESTextInput containerClassName="provider-sets-filters__input" label="Description" testId="provider-sets-filter__description" value={descriptionFilter} onKeyUp={(e) => handleSearchKeyUp(e)} onChange={(e) => setDescriptionFilter(e.target.value)} />
                <QESTextInput containerClassName="provider-sets-filters__input" label="Type" testId="provider-sets-filter__type" value={typeFilter} onKeyUp={(e) => handleSearchKeyUp(e)} onChange={(e) => setTypeFilter(e.target.value)} />
                <QESTextInput containerClassName="provider-sets-filters__input" label="Status" testId="provider-sets-filter__status" value={statusFilter} onKeyUp={(e) => handleSearchKeyUp(e)} onChange={(e) => setStatusFilter(e.target.value)} />
                <CheckboxDropdown
                  buttonClassNames="mr-2"
                  buttonText="Network Type"
                  cancelText="Cancel"
                  disableEmptySelection={true}
                  loading={loading}
                  items={getAllNetworkTypeDropdownItems()}
                  selectedItems={networkTypeFilter}
                  hideButtons={true}
                  onSelectionChanged={(selectedItems: DropdownItem<ResourceType>[]) => { setNetworkTypeFilter(selectedItems); }}
                />
                <QESButton qesType="primary" qesTestId="provider-sets__search-button" onClick={handleSearch}>Search</QESButton>
                <QESButton qesType="secondary" qesTestId="provider-sets__clear-filters" onClick={clearFilter} disabled={!isFiltered}>Clear</QESButton>
              </div>
              <p>Displaying {numRecordsDisplayed} of {totalRecords} provider networks {isFiltered ? "(filtered)" : ""}</p>
              <div className="row mt-3">
                <div className="col-12">
                  <AccordionContainer>
                    <Accordion
                      title="My Networks"
                      defaultOpen={activeFetchRequest === ProviderSetsRequestType.NetworkProviders || activeFetchRequest === ProviderSetsRequestType.ArchivedNetworkProviders}
                      onPanelWillOpen={() => { showActiveProviderSets ? setActiveFetchRequest(ProviderSetsRequestType.NetworkProviders) : setActiveFetchRequest(ProviderSetsRequestType.ArchivedNetworkProviders) }}
                      testId="provider-sets__my-networks-accordion"
                    >
                      <QESCheckbox
                        label={<><b>{activeSwitcherText}</b> <span>{activeSwitcherSubtext}</span></>}
                        id="active-network-toggle"
                        checked={showActiveProviderSets}
                        onChange={() => { setShowActiveProviderSets(!showActiveProviderSets); }}
                        disabled={loading}
                        testId="provider-sets__switcher"
                      />
                      {showActiveProviderSets ? myProviderSetsBody : archivedProviderSetsBody}
                    </Accordion>
                    <Accordion
                      title="Shared Networks"
                      defaultOpen={activeFetchRequest === ProviderSetsRequestType.SharedProviders}
                      onPanelWillOpen={() => { setActiveFetchRequest(ProviderSetsRequestType.SharedProviders); }}
                      testId="provider-sets__shared-networks-accordion"
                    >
                      {sharedProviderSetsBody}
                    </Accordion>
                    <RoleRestricted allowedRoles={[Roles.CLIENT_ADMIN, Roles.QUEST_ADMIN]}>
                      <Accordion
                        title="System Networks"
                        defaultOpen={activeFetchRequest === ProviderSetsRequestType.SystemProviders}
                        onPanelWillOpen={() => { setActiveFetchRequest(ProviderSetsRequestType.SystemProviders); }}
                        testId="provider-sets__system-networks-accordion"
                      >
                        {systemProviderSetsBody}
                      </Accordion>
                    </RoleRestricted>
                  </AccordionContainer>
                </div>
              </div>
            </PageSection>
          </PageInner>
        </Page>
      )}
    </>
  );
}

const getAllNetworkTypeDropdownItems = () => {
  const items: DropdownItem<ResourceType>[] = [];
  items.push({ key: ResourceType.ProviderSet, name: "Provider" });
  items.push({ key: ResourceType.FilteredProviderSet, name: "Filtered Provider" });

  return items;
}

const getSelectedNetworkTypeFilterItems = (selectedKeysJson: string) => {
  if (!selectedKeysJson)
    return getAllNetworkTypeDropdownItems();

  const selectedKeys = JSON.parse(selectedKeysJson);

  if (Array.isArray(selectedKeys) && selectedKeys.length > 0)
    return getAllNetworkTypeDropdownItems().filter(item => selectedKeys.some(x => x.key === item.key));
  else
    return getAllNetworkTypeDropdownItems();
}